import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

export const useDemoPlayerState = ({
  introductionShown,
  onIntroductionComplete,
}: DemoPlayerStateParams) => {
  const [ introduction, setIntroduction ] = useState(!introductionShown)
  const [ paused, setPaused ] = useState(true)
  const [ currentStep, setCurrentStep ] = useState(0)
  const [ startStep, setStartStep ] = useState(0)
  const [ run, setRun ] = useState(0)
  const [ finished, setFinished ] = useState(false)
  const [ summaryVisible, setSummaryVisible ] = useState(false)
  const [ finalOfferVisible, setFinalOfferVisible ] = useState(false)
  const [ completedStep, setCompletedStep ] = useState<number | null>(null)
  const [ pendingCompletedStep, setPendingCompletedStep ] = useState<number | null>(null)
  const [ speed, setSpeed ] = useState(1)
  const [ recalculating, setRecalculating ] = useState(false)
  const [ recalculationDelay, setRecalculationDelay ] = useState(false)

  const showRecalculation = recalculating || recalculationDelay
  const toolbarBlocked = introduction || showRecalculation
  const clock = useMemo<DemoPlayerClock>(() => ({
    controller: new AbortController(),
    manualScrollUntil: 0,
    paused: true,
    speed: 1,
  }), [ run ])

  clock.paused = paused || pendingCompletedStep !== null
  clock.speed = speed

  useEffect(() => {
    if (!recalculationDelay) {
      return
    }

    const timer = window.setTimeout(() => setRecalculationDelay(false), 3000)

    return () => window.clearTimeout(timer)
  }, [ recalculationDelay ])

  const complete = useCallback(() => {
    setFinished(true)
  }, [])

  const completeStep = useCallback((index: number) => {
    clock.paused = true
    setPendingCompletedStep(index)
  }, [ clock ])

  useEffect(() => {
    if (pendingCompletedStep === null) {
      return
    }

    const timer = window.setTimeout(() => {
      setPaused(true)
      setCompletedStep(pendingCompletedStep)
      setPendingCompletedStep(null)
    }, pendingCompletedStep === 2 ? 1000 : 2000)

    return () => window.clearTimeout(timer)
  }, [ pendingCompletedStep ])

  const player = useMemo<DemoPlayerContextValue>(() => ({
    clock,
    startStep,
    summaryVisible,
    onStep: setCurrentStep,
    onComplete: complete,
    onStepComplete: completeStep,
  }), [ clock, startStep, summaryVisible, complete, completeStep ])

  const pause = useCallback(() => {
    clock.paused = true
    setPaused(true)
  }, [ clock ])

  const seek = useCallback((index: number) => {
    setPendingCompletedStep(null)
    setCompletedStep(null)
    setFinished(false)
    setSummaryVisible(false)
    setFinalOfferVisible(false)
    clock.controller.abort()
    setCurrentStep(index)
    setStartStep(index)
    setRun(value => value + 1)
  }, [ clock ])

  const restartCurrentStep = () => {
    setPendingCompletedStep(null)
    setCompletedStep(null)
    setFinished(false)
    setSummaryVisible(false)
    setFinalOfferVisible(false)
    clock.controller.abort()
    setStartStep(currentStep)
    setRun(value => value + 1)
  }

  const handleRecalculatingChange = (value: boolean) => {
    setRecalculating(value)

    if (value) {
      setSummaryVisible(false)
      setFinalOfferVisible(false)
      setRecalculationDelay(true)
      setPendingCompletedStep(null)
      setCompletedStep(null)
    }
  }

  const begin = () => {
    setIntroduction(false)
    onIntroductionComplete()
  }

  const play = useCallback(() => {
    if (finished && !summaryVisible) {
      seek(0)
    }

    setPaused(false)
  }, [ finished, summaryVisible, seek ])

  const continuePlayback = useCallback(() => {
    if (completedStep === null) {
      return
    }

    if (completedStep === 6) {
      setCompletedStep(null)
      setFinished(false)
      setSummaryVisible(true)
      setPaused(false)
      return
    }

    const nextStep = completedStep + 1
    clock.controller.abort()
    setCompletedStep(null)
    setFinished(false)
    setCurrentStep(nextStep)
    setStartStep(nextStep)
    setRun(value => value + 1)
    setPaused(false)
  }, [ clock, completedStep ])

  const hasSummaryOverlay = completedStep !== null || finalOfferVisible
  const hasPlaybackOverlay = introduction || hasSummaryOverlay
  const showPauseOverlay = paused && !hasPlaybackOverlay

  const finishSummary = useCallback(() => {
    clock.paused = true
    setPaused(true)
    setFinished(true)
    setFinalOfferVisible(true)
  }, [ clock ])

  const closeFinalOffer = () => {
    setFinalOfferVisible(false)
    setPaused(false)
  }

  return {
    begin,
    clock,
    closeFinalOffer,
    completedStep,
    continuePlayback,
    currentStep,
    finalOfferVisible,
    finishSummary,
    handleRecalculatingChange,
    introduction,
    pause,
    paused,
    play,
    player,
    restartCurrentStep,
    run,
    seek,
    setSpeed,
    showPauseOverlay,
    showRecalculation,
    speed,
    summaryVisible,
    toolbarBlocked,
  }
}
